import React from 'react';
import { IProduct } from '@kizi/comparator-core';
import { Div, Image, P } from '@kizi/core-components';
import {
  sendCustomEvent,
  Sustainability,
  Price,
  ProsCons,
  DocumentUrl,
} from '../../../components';
import { ComparisonSubmitButton } from '../../../components/comparison/ComparisonSubmitButton';
import { onClick } from '../../product';
import { formatPrice } from '../../global';
import { handleProsNCons } from '../energy/helpers';
import { IconV2 as Icon } from '@kizi/shared-components';

const onApplyClick = async (service, data: IProduct) => {
  const interpreter = service.state.context.steps[3];

  if (data.affiliateUrl) {
    sendCustomEvent('affiliate-apply', {
      selectedProduct: data,
    });
    const overviewIndex = service._state.context.steps.findIndex(
      (step) => step.id === 'productComparison',
    );
    service.send([
      {
        type: 'GOTO',
        index: overviewIndex,
      },
    ]);

    const url = new URL(window.location.href);
    if (url.searchParams.has('affiliateUrl')) {
      return;
    }
    url.searchParams.set('step', 'overview');

    window.open(`${url.toString()}&affiliateUrl`);
  } else {
    onClick(interpreter, service, data, 5);
  }
};

export const getProductComparisonOverview = (data, _labels, priceMode) => {
  const {
    productDescription,
    image,
    purchasableProduct,
    name,
    totalInstallmentAmount,
    oneOffCostsAmount,
    bigProductFeature,
    testScore,
  } = data;

  const priceLabel = priceMode === 'month' ? 'maand' : 'jaar';

  return [
    {
      content: (
        <Div display="flex" flexDirection="column" alignItems="center">
          <Div display="flex" variant="productLogoWrapper" mb={[2, null, 5]}>
            {image ? (
              <Image
                alt={`logo-${name}`}
                height="100%"
                src={image}
                width="100%"
              />
            ) : (
              <Div height={40} />
            )}
          </Div>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{productDescription}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Price
          // discount={discountIncluded}
          label={`per ${priceLabel}`}
          value={totalInstallmentAmount}
          variant="priceBig"
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{formatPrice(oneOffCostsAmount)}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{formatPrice(bigProductFeature.value[priceMode])}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Sustainability
          score={String(testScore)}
          variant="sustainabilityRegular"
          iconName="os-icon"
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: data.purchasableProduct ? (
        <Div display="flex" justifyContent="center">
          <ComparisonSubmitButton
            data={data}
            fieldName="overview.selectedProduct"
            nextIndex={5}
            onSubmitFn={onApplyClick}
            isProductEnabled={purchasableProduct}
          />
        </Div>
      ) : (
        <Div display="flex" justifyContent="center">
          <Div>Geen samenwerking met verzekeraar</Div>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

const getProductComparisonInsurer = (data: IProduct) => {
  const { provider, prosAndCons, characteristics } = data;
  const { description } = provider;

  const allowed = ['title1', 'title2'];
  const pros = handleProsNCons(prosAndCons?.pros, allowed);
  const cons = handleProsNCons(prosAndCons?.cons, allowed);

  const communication = characteristics?.find(
    (char) => char.label === 'Communicatie',
  );

  const polis = characteristics?.find((char) => char.label.includes('polis'));

  return [
    {
      content: <P>{description}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: prosAndCons && (
        <Div display="flex" flexDirection="column">
          {pros && <ProsCons data={pros} onlyPros />}
          {cons && <ProsCons data={cons} onlyCons />}
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: communication && <P>{communication?.value}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: polis && <P>{polis?.value}</P>,
      isVisible: true,
      toggleable: false,
    },
  ];
};

const getProductComparisonPremium = (data) => {
  const { totalInstallmentAmount, oneOffCostsAmount } = data;
  return [
    {
      content: <P>{formatPrice(totalInstallmentAmount)}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{formatPrice(totalInstallmentAmount)}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{formatPrice(oneOffCostsAmount)}</P>,
      isVisible: true,
      toggleable: false,
    },
  ];
};

const getProductComparisonMainConditions = (
  data: IProduct,
  _labels,
  priceMode,
  additionalData,
) => {
  const { coverageAreaCode, numberOfDaysCoverage } = additionalData;

  const { productType, provider, bigProductFeature } = data;
  const {
    insuranceCardUrl = '',
    termsAndConditionsUrl = '',
    sellingPoints,
  } = provider;

  function getLabelByType(productType = '') {
    switch (productType) {
      case 'insurance/damage/building':
        return ' Woonhuis';
      case 'insurance/damage/home':
        return ' Inboedel';
      case 'insurance/damage/liability':
        return ' Aansprakelijkheid';
      default:
        return '';
    }
  }
  const urlLabel = getLabelByType(productType);

  const daysToMonthsText = (value) => {
    const mapping = {
      45: 'Maximaal 6 weken',
      60: 'Maximaal 2 maanden',
      90: 'Maximaal 3 maanden',
      180: 'Maximaal 6 maanden',
    };

    return mapping[value] || 'Maximaal 12 maanden';
  };

  return [
    {
      content: (
        <>
          {sellingPoints?.map(({ description, value }) => {
            const iconMapping = {
              'Yes': 'green-check',
              'No': 'small-cancel',
              'Warning': 'warning',
            };

            return (
              <Div key={description} display="flex" alignItems="center">
                <Icon name={iconMapping[value]} size="28px" mr={1} />
                <P>{description}</P>
              </Div>
            );
          })}
        </>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{coverageAreaCode === 'VX' ? 'Europa' : 'Wereld'}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{daysToMonthsText(numberOfDaysCoverage)}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P>{formatPrice(bigProductFeature.value[priceMode])}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <>
          <DocumentUrl
            href={insuranceCardUrl}
            label={'Bekijk dekkingskaart' + urlLabel}
            mb={3}
          />
          <DocumentUrl
            href={termsAndConditionsUrl}
            label={'Polisvoorwaarden' + urlLabel}
          />
        </>
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

// const getProductComparisonPolicyConditions = (data: IProduct) => {
//   const { fees } = data;
//   console.log('fees', fees);

//   const basisdekking = fees?.find((el) => el.label === 'Dekkingsmogelijkheden');
//   console.log('basisdekking', basisdekking);

//   return [
//     {
//       content: <P>Test</P>,
//       isVisible: true,
//       toggleable: false,
//     },
//   ];
// };

export const osTravelHelpers = {
  overview: getProductComparisonOverview,
  insurer: getProductComparisonInsurer,
  premium: getProductComparisonPremium,
  mainConditions: getProductComparisonMainConditions,
  // policyConditions: getProductComparisonPolicyConditions,
};
